import {
  fetchFormTaslak,
  saveFormTaslak,
  deleteFormTaslak,
} from './formTaslakService';
import {
  SIPARIS_TASLAK_DEFS,
  FORM_TASLAK_TIP_SIPARIS_LEGACY,
  getSiparisTaslakDef,
  getSiparisTaslakTipFromFlags,
  taslakHasContent,
  countTaslakParca,
} from '../constants/siparisTaslak';
import {
  loadSiparisDraftPhotosRaw,
  clearSiparisDraftPhotos,
} from '../utils/siparisDraftStorage';
import { fetchProduct } from './productsService';
import {
  netFiyatFromParcaKaynak,
  netFiyatFromIcerik,
} from '../utils/siparisNetFiyat';

async function fetchNetFiyatByIcerikId(icerikId) {
  if (!icerikId) return null;
  try {
    const res = await fetchProduct(icerikId);
    const doc = res?.veri?.icerik || res?.veri?.urun || res?.veri;
    return netFiyatFromIcerik(doc);
  } catch {
    return null;
  }
}

/** Doğrudan alım taslağı için gorunenFiyat (net); garanti/konsinye için null */
async function resolveGorunenFiyatForTaslak(parca, def) {
  if (def.garantiDahilinde || def.konsinye) return null;
  const fromKaynak = netFiyatFromParcaKaynak(parca);
  if (fromKaynak != null) return fromKaynak;
  const icerikId = parca?.icerikId || parca?.id || parca?._id;
  return fetchNetFiyatByIcerikId(icerikId);
}

/** Taslaktan yüklenen satırlarda eksik net fiyatları tamamlar */
export async function enrichSecilenParcalarNetFiyat(parcalar) {
  if (!Array.isArray(parcalar) || parcalar.length === 0) return parcalar;
  return Promise.all(
    parcalar.map(async (p) => {
      if (p.gorunenFiyat != null && !Number.isNaN(parseFloat(p.gorunenFiyat))) return p;
      const fiyat = await fetchNetFiyatByIcerikId(p.icerikId || p.id);
      if (fiyat == null) return p;
      return { ...p, gorunenFiyat: fiyat };
    }),
  );
}

/** @deprecated TP artık net fiyat kullanır — enrichSecilenParcalarNetFiyat */
export const enrichSecilenParcalarBrutFiyat = enrichSecilenParcalarNetFiyat;

const emptyDraftVeri = (def) => ({
  step: def.garantiDahilinde ? 2 : 2,
  garantiDahilinde: def.garantiDahilinde,
  konsinye: def.konsinye,
  servisKaydiId: null,
  servisKaydiSnapshot: null,
  secilenParcalar: [],
  not: '',
  farkliTeslimatAdresi: false,
  ozelTeslimatAdresi: '',
});

/** Eski tek `siparis` taslağını türe göre yeni anahtarlara taşır */
export async function migrateLegacySiparisTaslakIfNeeded() {
  try {
    const legacy = await fetchFormTaslak(FORM_TASLAK_TIP_SIPARIS_LEGACY);
    const v = legacy?.veri?.veri;
    if (!v) return;
    const tip = getSiparisTaslakTipFromFlags(!!v.garantiDahilinde, !!v.konsinye);
    const existing = await fetchFormTaslak(tip);
    if (!existing?.veri?.veri) {
      await saveFormTaslak(tip, v);
    }
    await deleteFormTaslak(FORM_TASLAK_TIP_SIPARIS_LEGACY);
  } catch {
    /* ignore */
  }
}

export async function fetchSiparisTaslakOzetleri() {
  await migrateLegacySiparisTaslakIfNeeded();
  const list = await Promise.all(
    SIPARIS_TASLAK_DEFS.map(async (def) => {
      try {
        const res = await fetchFormTaslak(def.tip);
        const veri = res?.veri?.veri || null;
        const photoCount = loadSiparisDraftPhotosRaw(def.tip).length;
        return {
          ...def,
          veri,
          photoCount,
          parcaAdet: countTaslakParca(veri),
          dolu: taslakHasContent(veri, photoCount),
          guncellenmeTarihi: res?.veri?.guncellenmeTarihi || null,
        };
      } catch {
        return {
          ...def,
          veri: null,
          photoCount: 0,
          parcaAdet: 0,
          dolu: false,
          guncellenmeTarihi: null,
        };
      }
    }),
  );
  return list;
}

export async function appendParcaToSiparisTaslak(taslakTip, parca, adet = 1) {
  const def = getSiparisTaslakDef(taslakTip);
  if (!def) throw new Error('Geçersiz taslak tipi');
  const icerikId = parca?.icerikId || parca?.id || parca?._id;
  if (!icerikId) throw new Error('Parça kimliği bulunamadı');

  const numAdet = Math.max(1, parseInt(adet, 10) || 1);
  let existingRes = null;
  try {
    existingRes = await fetchFormTaslak(taslakTip);
  } catch {
    existingRes = null;
  }

  const veri = existingRes?.veri?.veri
    ? { ...existingRes.veri.veri }
    : emptyDraftVeri(def);

  veri.garantiDahilinde = def.garantiDahilinde;
  veri.konsinye = def.konsinye;

  const list = Array.isArray(veri.secilenParcalar) ? [...veri.secilenParcalar] : [];
  const idx = list.findIndex((p) => String(p.icerikId || p.id) === String(icerikId));
  const gorunenFiyat = await resolveGorunenFiyatForTaslak(parca, def);
  if (idx >= 0) {
    list[idx] = {
      ...list[idx],
      adet: (list[idx].adet || 1) + numAdet,
      gorunenFiyat: list[idx].gorunenFiyat ?? gorunenFiyat,
    };
  } else {
    list.push({
      icerikId: String(icerikId),
      parcaAdi: parca.parcaAdi || parca.icerikAdi || 'Parça',
      parcaKodu: parca.parcaKodu || parca.icerikKodu || '',
      adet: numAdet,
      gorunenFiyat,
    });
  }
  veri.secilenParcalar = list;

  if (def.garantiDahilinde) {
    veri.step = veri.servisKaydiSnapshot || veri.servisKaydiId ? 3 : 2;
  } else {
    veri.step = 2;
  }

  await saveFormTaslak(taslakTip, veri);
  return veri;
}

/** Sunucu taslağı + cihazdaki sipariş fotoğraf önbelleğini siler */
export async function clearSiparisTaslak(taslakTip) {
  await deleteFormTaslak(taslakTip).catch(() => {});
  clearSiparisDraftPhotos(taslakTip);
}
